import { useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/Header";
import Footer from "../components/Footer";
import { TypewriterEffect } from "../components/TypewriterEffect";
import ImageMarquee from "../components/ImageMarquee";
import StrawberrySection from "../components/StrawberrySection";
import SEO from "../components/SEO";
import {
  pillarCards,
  diningIntro,
  diningMenus,
  idealForGroups,
} from "../data/siteContent";

const gawkariRoomImages = [
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908943/WhatsApp_Image_2025-12-14_at_5.56.05_PM_zortcw.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908943/WhatsApp_Image_2025-12-14_at_5.55.59_PM_1_mzwcs0.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908942/WhatsApp_Image_2025-12-14_at_5.56.05_PM_1_jjold4.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908940/WhatsApp_Image_2025-12-14_at_5.56.03_PM_wadhse.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908938/WhatsApp_Image_2025-12-14_at_5.55.55_PM_p5o2hf.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908936/WhatsApp_Image_2025-12-14_at_5.56.01_PM_1_yrtkiu.jpg",
];

export default function HomePage() {
  return (
    <>
      <SEO />
      <SiteHeader />
      <main style={{ paddingTop: 0 }}>
        <Hero />
        <AccommodationGlimpse />
        <IdealForSection />
        <GawkariRoomsShowcase />
        <DiningHighlights />
        <StrawberrySection />
        <Highlights />
        <CTASection />
      </main>
      <Footer />
    </>
  );
}

function Hero() {
  const titleWords = useMemo(
    () => [
      { text: "Welcome" },
      { text: "to" },
      { text: "Gawkari" },
      { text: "Hillside" },
    ],
    []
  );

  /* React often needs explicit muted attribute handling for autoplay policy */
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.defaultMuted = true;
      videoRef.current.muted = true;
      videoRef.current.play().catch(e => console.log("Autoplay blocked:", e));
    }
  }, []);


  return (
    <section className="position-relative d-flex align-items-center bg-dark" style={{ minHeight: "90vh", overflow: 'hidden' }}>
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 0 }}>
        <video
          ref={videoRef}
          src="https://www.pexels.com/download/video/9973867/"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlayThrough={() => {
            if(videoRef.current) videoRef.current.play().catch(e => console.log("Play failed", e));
          }}
          className="w-100 h-100 object-fit-cover"
        />
      </div>
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: "rgba(0, 0, 0, 0.4)", zIndex: 1 }} />

      <div className="container position-relative text-white" style={{ zIndex: 2, marginTop: "5rem", marginBottom: "5rem" }}>
        <div style={{ marginTop: "150px" }}>
          <div className="mt-4 text-center">
            <div className="mb-4 text-center" style={{ fontFamily: '"Rubik", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', fontSize: "2.5rem", fontWeight: 700, color: "#f4d6bc" }}>
               <TypewriterEffect
                words={titleWords}
                className="text-center"
                cursorClassName="bg-cream"
              />
            </div>
          
            {/* Fallback title if Typewriter is just the eyebrow, we might want a main title? 
                Looking at previous code, Typewriter WAS the eyebrow/title combo.
                But wait, previous CSS had .hero__title separately? 
                No, the previous HTML structure had:
                <TypewriterEffect ... className="hero__eyebrow ..." />
                
                Code snippet 987 shows:
                <TypewriterEffect words={...} className="hero__eyebrow ..." />
                
                The 'words' are "Welcome to Gawkari Hillside".
                The CSS .hero__eyebrow was font-family: script.
                
                So the main big text WAS the script text.
            */}

            <p className="lead mb-5 mx-auto" style={{ color: "rgba(255,255,255,0.95)", textShadow: "2px 2px 4px rgba(0,0,0,0.8)", maxWidth: '600px', fontSize: "1.125rem", lineHeight: 1.8 }}>
              Experience authentic Maharashtrian hospitality amidst the serene Sahyadri hills. Your perfect rural escape awaits.
            </p>

            <div className="d-flex gap-3 justify-content-center mt-4">
              <Link className="btn btn-secondary px-4 py-2" to="/enquiry">
                Plan Your Visit
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AccommodationGlimpse() {
  return (
    <section className="py-5 bg-light">
      <div className="container">
        <div className="text-center mb-5">
          <h2 className="display-5 fw-bold text-dark">Take a look at our rooms</h2>
          <p className="text-secondary mx-auto" style={{ maxWidth: '640px' }}>See the actual rooms where you'll stay and enjoy true Gawkari hospitality</p>
        </div>
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {gawkariRoomImages.slice(0, 6).map((image, index) => (
            <div key={index} className="col">
              <div className="card border-0 shadow-sm h-100 overflow-hidden rounded-3">
                <div className="ratio ratio-4x3">
                  <img
                    src={image}
                    className="object-fit-cover opacity-85"
                    onMouseEnter={(e) => {
                      e.currentTarget.classList.remove("opacity-85");
                      e.currentTarget.classList.add("opacity-100");
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.classList.remove("opacity-100");
                      e.currentTarget.classList.add("opacity-85");
                    }}
                    alt={`Gawkari room ${index + 1} - Authentic accommodation at Hillside`}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-5">
          <Link className="btn btn-primary" to="/stay">
            View All Accommodations
          </Link>
        </div>
      </div>
    </section>
  );
}

function Highlights() {
  return (
    <section className="py-5 pt-0">
      <div className="container">
        <div className="text-center mb-5">
          <span className="badge bg-green-lt mb-2 p-2">Why Gawkari Hillside</span>
          <h2 className="display-5 fw-bold text-dark">An authentic Maharashtrian escape</h2>
          <div className="text-secondary mx-auto mt-3" style={{ maxWidth: '640px' }}>
            Gawkari Hillside gives you calm nature, clean stays, tasty Maharashtrian food, and a friendly environment.
            <br />
            A perfect place to relax and enjoy slow, peaceful days.
          </div>
        </div>
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {pillarCards.map((card) => (
            <div className="col" key={card.title}>
              <article className="card h-100 p-4 border-0 shadow-sm">
                <div className="display-4 text-primary mb-3">
                  {card.icon}
                </div>
                <h3 className="h3 mb-3">{card.title}</h3>
                <p className="text-muted mb-3">{card.subtitle}</p>
                <ul className="list-unstyled mb-0">
                  {card.points.slice(0, 3).map((point) => (
                    <li key={point} className="d-flex align-items-start mb-2">
                      <span className="text-primary me-2">•</span>
                      <span className="text-secondary">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DiningHighlights() {
  const diningImages = [
    "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80",
  ];
  
  return (
    <section className="py-5 bg-white" id="dining">
      <div className="container">
        <div className="text-center mb-5">
          <span className="badge bg-green-lt mb-2 p-2">Taste the real Maharashtra</span>
          <h2 className="display-5 fw-bold text-dark">Every meal is a celebration</h2>
          <p className="text-secondary mt-3 mx-auto" style={{ maxWidth: '640px' }}>{diningIntro}</p>
        </div>
        <div className="row row-cols-1 row-cols-md-3 g-4">
          {diningMenus.map((menu, index) => (
            <div className="col" key={menu.title}>
              <article className="card h-100 border-0 shadow-sm">
                <div className="card-img-top ratio ratio-16x9 overflow-hidden rounded-top">
                  <img src={diningImages[index % diningImages.length]} className="object-fit-cover w-100 h-100" alt="Dining highlight" loading="lazy" />
                </div>
                <div className="card-body p-4">
                  <h3 className="h3 card-title text-primary mb-3">{menu.title}</h3>
                  <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
                    {menu.items.slice(0, 3).map((item) => (
                      <li key={item} className="d-flex align-items-center text-secondary">
                        <span className="badge bg-secondary-lt p-1 me-2 rounded-circle" style={{ width: '6px', height: '6px' }}> </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </div>
          ))}
        </div>
             
        <div className="d-flex justify-content-center mt-5">
          <Link to="/dining" className="btn btn-primary">
            Explore More
          </Link>
        </div>
      </div>
    </section>
  );
}

function IdealForSection() {
  return (
    <section className="py-5 bg-light">
      <div className="container">
        <div className="text-center mb-5">
          <span className="badge bg-green-lt mb-2 p-2">Book Rooms, Tents & Camps</span>
          <h2 className="display-5 fw-bold text-dark">Find Your Perfect Stay at Gawkari Hillside</h2>
        </div>
        <div className="row row-cols-1 row-cols-md-3 g-4">
          {idealForGroups.map((group) => (
            <div className="col" key={group.title}>
              <article className="card h-100 p-4 text-center border-0 shadow-sm">
                <span className="d-block mb-3 display-4 text-primary" aria-hidden>
                  {group.icon}
                </span>
                <h3 className="h3 mb-3">{group.title}</h3>
                <p className="text-secondary mb-0">{group.description}</p>
              </article>
            </div>
          ))}
        </div>
        
        <div className="d-flex justify-content-center mt-5">
          <Link to="/enquiry" className="btn btn-primary">
            Book Now
          </Link>
        </div>
      </div>
    </section>
  );
}

function GawkariRoomsShowcase() {
  return (
    <section className="py-5 bg-white overflow-hidden">
      <div className="container">
        <div className="text-center mb-5">
          <span className="badge bg-green-lt mb-2 p-2">Experience Our Authentic Rooms</span>
          <h2 className="display-5 fw-bold text-dark">Comfortable Stays in Traditional Settings</h2>
          <p className="text-secondary mt-3 mx-auto" style={{ maxWidth: '640px' }}>
            Discover our beautifully designed rooms that blend traditional Maharashtrian architecture with modern comfort. 
            Each space offers a unique connection to nature while ensuring a peaceful and relaxing stay.
          </p>
        </div>
      </div>
      
      <div className="w-100 py-4 bg-light">
        <ImageMarquee 
          images={gawkariRoomImages} 
          speed={40} 
          direction="left"
          className="w-100"
        />
      </div>
      
      <div className="container">
        <div className="text-center pt-5">
          <Link className="btn btn-primary" to="/stay">
            Explore All Accommodations
          </Link>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-5 bg-light border-top">
      <div className="container">
        <div className="text-center mx-auto" style={{ maxWidth: '720px' }}>
          <h2 className="display-5 fw-bold text-dark mb-3">Ready to craft your Gawkari story?</h2>
          <p className="text-secondary mb-4 fs-3">
            Share your travel window, group size, and interests. Our hosts will respond within 24 hours with a curated
            village itinerary.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Link className="btn btn-primary px-4 py-2" to="/enquiry">
              Book now
            </Link>
            <Link className="btn btn-outline-secondary px-4 py-2" to="/gallery">
              Browse gallery
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
